"use client";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useSelector, useDispatch } from "react-redux";
import { useRouter, usePathname } from "next/navigation";
import { Folder, Loader } from "lucide-react";
import { addProject, setShowProjectPopup, setActiveTab } from "@/store/mainSlice";


export default function ProjectPopup({ open, onClose }) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.main);

  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!name.trim() || loading) return;

    setLoading(true);

    try {
      let payload = {
        user_id: userData._id,
        category: name.trim(),
      };

      const res = await fetch(
        `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/project/create`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();
      if (data.status !== 1) {
        toast.error(data.message || "Something went wrong", {
          style: { color: "red" },
          iconTheme: { primary: "red", secondary: "#fff" },
          position: "top-center",
        });
        setLoading(false);
        return;
      }

      const newProject = data.project;
      dispatch(addProject(newProject));
      localStorage.setItem("projectName", newProject.category);
      localStorage.setItem("parent_scr_id", newProject.parent_scr_id);
      dispatch(setActiveTab("Chat"));
      window.dispatchEvent(
        new CustomEvent("project-created", {
          detail: {
            parentScrId: newProject.parent_scr_id,
            project: newProject,
          },
        })
      );

      router.push(`/project/${newProject.parent_scr_id}`);
      dispatch(setShowProjectPopup(false));
      setName("");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong", { style: { color: "red" } });
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
//   useEffect(() => {
//   const finishRedirect = () => {
//     setLoading(false);
//     dispatch(setShowProjectPopup(false)); // Close popup AFTER redirect
//   };

//   window.addEventListener("project-redirect", finishRedirect);
//   return () =>
//     window.removeEventListener("project-redirect", finishRedirect);
// }, []);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="w-[685px] rounded-2xl p-6 shadow-xl border bg-white dark:bg-[#1e1e1f] border-black/10 dark:border-white/10 text-black dark:text-white">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Agent name</h2>
          <button
            onClick={onClose}
            className="text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white transition"
          >
            ✕
          </button>
        </div>

        {/* Input */}
        <div className="relative w-full">
          <div className="w-full bg-accent dark:bg-[#2a2a2b] border border-black/10 dark:border-white/10
              px-4 py-3 rounded-xl flex items-center gap-3 transition-all duration-200
    focus-within:border-black/20 dark:focus-within:border-white/20  focus-within:shadow-[0_0_0_2px_rgba(0,0,0,0.07)]
            dark:focus-within:shadow-[0_0_0_2px_rgba(255,255,255,0.1)]">
            {/* Icon block */}
            <span className="text-lg">
              <Folder className="text-gray-500 dark:text-gray-300" />
            </span>

            {/* TEXT INPUT */}
            <input
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Copenhagen Trip"
              className="flex-1 bg-transparent outline-none text-black dark:text-white placeholder-gray-500 dark:placeholder-gray-400 chat-input cursor-text"
              onKeyDown={(e) => {
                // if (e.key === "Enter" && !e.shiftKey) handleSubmit();
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />

            {/* Clear button */}
            {name && (
              <button
                onClick={() => setName("")}
                className="text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white transition"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Info box */}
        <div className="p-3 rounded-xl mt-4 bg-accent text-gray-700
          dark:bg-[#2c2c2d] dark:text-gray-300 text-sm flex gap-2">
          💡
          <span>
            Agents keep chats, files, and custom instructions in one place.
            Use them to keep things organized.
          </span>
        </div>

        {/* Create button */}
        <div className="text-right">
          <button
            type="submit"
            className={`inline-flex mt-4 px-6 py-2 rounded-full text-sm font-medium transition items-center gap-2 justify-center
              ${name.trim()
                ? "bg-black text-white dark:bg-white dark:text-black cursor-pointer"
                : "bg-gray-300 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed opacity-50"
              }`}
            disabled={!name.trim() || loading}
            onClick={handleSubmit}
          >
            {loading ? (
              <>
                <span>Creating...</span>
                <Loader className="animate-spin h-4 w-4" />
              </>
            ) : (
              "Create Agent"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
